
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as ReactRouterDom from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { 
  Target, Search, Filter as FilterIcon, SortAsc, ChevronRight, Download, Upload,
  Calendar, Briefcase, X, ChevronDown, Clock, SlidersHorizontal, RotateCcw 
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { EngagementFilterSidebar, EngagementFilters } from '../features/programs/EngagementFilterSidebar';
import Papa from 'papaparse';
import { formatDateSafe } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const { useNavigate } = ReactRouterDom as any;

const DEFAULT_FILTERS: EngagementFilters = {
  search: '',
  programIds: [],
  programMatch: 'any',
  statuses: [],
  statusMatch: 'any',
  audienceTypeIds: [],
  audienceMatch: 'any',
  categoryIds: [],
  categoryMatch: 'any',
  tags: [],
  tagMatch: 'any',
  startDate: '',
  endDate: '',
  noSessionsOnly: false
};

const EngagementsList: React.FC = () => {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'system_admin';
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Persistence Helpers
  const getSaved = (key: string, def: any) => {
    const val = localStorage.getItem(`eng_list_${key}`);
    if (val === null) return def;
    try { return JSON.parse(val); } catch { return val; }
  };

  const [filters, setFilters] = useState<EngagementFilters>(() => getSaved('advanced_filters', DEFAULT_FILTERS));
  const [sortField, setSortField] = useState<string>(() => getSaved('sortField', 'created_at'));
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => getSaved('sortDirection', 'desc'));
  const [page, setPage] = useState(() => getSaved('page', 1));
  const [pageSize, setPageSize] = useState(() => getSaved('pageSize', 30));
  
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 250,
    program: 200,
    status: 120,
    sessions: 120,
    startDate: 120,
    endDate: 120
  });

  // Sync to LocalStorage
  useEffect(() => { localStorage.setItem('eng_list_advanced_filters', JSON.stringify(filters)); }, [filters]);
  useEffect(() => { localStorage.setItem('eng_list_sortField', JSON.stringify(sortField)); }, [sortField]);
  useEffect(() => { localStorage.setItem('eng_list_sortDirection', JSON.stringify(sortDirection)); }, [sortDirection]);
  useEffect(() => { localStorage.setItem('eng_list_page', JSON.stringify(page)); }, [page]);
  useEffect(() => { localStorage.setItem('eng_list_pageSize', JSON.stringify(pageSize)); }, [pageSize]);

  const { data: engagementsData, isLoading } = useQuery({
    queryKey: ['engagements-list-v4', filters, sortField, sortDirection, page, pageSize],
    queryFn: async () => {
      // Fetch core relations
      let q = supabase.from('engagements').select('*, programs(name), sessions(id), engagement_categories(label), audience_types(label), engagement_tag_links(tag:tag_id(label))');
      
      // Basic Search (Always applies)
      if (filters.search) {
        q = q.ilike('name', `%${filters.search}%`);
      }

      // We optimize typical OR cases by using .in() at the DB level
      // If "Match All" is used on a single-value column with multiple selections, it's logically impossible
      if (filters.programIds.length > 0 && filters.programMatch === 'any') {
        q = q.in('program_id', filters.programIds);
      }
      if (filters.statuses.length > 0 && filters.statusMatch === 'any') {
        q = q.in('status', filters.statuses);
      }
      if (filters.audienceTypeIds.length > 0 && filters.audienceMatch === 'any') {
        q = q.in('audience_type_id', filters.audienceTypeIds);
      }
      if (filters.categoryIds.length > 0 && filters.categoryMatch === 'any') {
        q = q.in('category_id', filters.categoryIds);
      }

      const { data, error } = await q.order(sortField, { ascending: sortDirection === 'asc' });

      if (error) throw error;

      let result = data || [];
      
      // Client-side filtering for more complex criteria (date ranges, tags, AND logic on single-select fields)
      result = result.filter(e => {
        // Enforce AND Logic for fields if multiple selected (Single-value fields usually return 0)
        if (filters.programMatch === 'all' && filters.programIds.length > 0) {
          if (!filters.programIds.includes(e.program_id)) return false;
        }
        if (filters.statusMatch === 'all' && filters.statuses.length > 0) {
          if (!filters.statuses.includes(e.status)) return false;
        }
        if (filters.audienceMatch === 'all' && filters.audienceTypeIds.length > 0) {
          if (!filters.audienceTypeIds.includes(e.audience_type_id)) return false;
        }
        if (filters.categoryMatch === 'all' && filters.categoryIds.length > 0) {
          if (!filters.categoryIds.includes(e.category_id)) return false;
        }

        // Date Range
        if (filters.startDate && e.start_date && e.start_date < filters.startDate) return false;
        if (filters.endDate && e.end_date && e.end_date > filters.endDate) return false;

        // Tags
        if (filters.tags.length > 0) {
          const engTags = Array.isArray(e.engagement_tag_links)
            ? e.engagement_tag_links.map((link: any) => link.tag?.label).filter(Boolean)
            : [];
          if (filters.tagMatch === 'any') {
            const hasAny = filters.tags.some(t => engTags.includes(t));
            if (!hasAny) return false;
          } else {
            const hasAll = filters.tags.every(t => engTags.includes(t));
            if (!hasAll) return false;
          }
        }

        // Sessions Isolation
        if (filters.noSessionsOnly) {
          if (e.sessions && e.sessions.length > 0) return false;
        }

        return true;
      });

      const total = result.length;
      const from = (page - 1) * pageSize;
      const to = from + pageSize;
      const paginatedResult = result.slice(from, to);

      return { engagements: paginatedResult, total };
    }
  });

  const engagements = engagementsData?.engagements;
  const totalCount = engagementsData?.total || 0;

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleResize = (column: string, width: number) => {
    setColumnWidths(prev => ({ ...prev, [column]: Math.max(100, width) }));
  };

  const handleExport = () => {
    if (!engagements) return;
    const data = engagements.map(e => ({
      'Engagement Name': e.name,
      'Program': e.programs?.name || '—',
      'Status': e.status || 'Active',
      'Audience': e.audience_types?.label || '—',
      'Structure': e.engagement_categories?.label || '—',
      'Tracking Mode': e.tracking_mode,
      'Sessions Count': e.sessions?.length || 0,
      'Start Date': e.start_date || '—',
      'End Date': e.end_date || '—'
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `hfli_engagements_${new Date().toISOString().slice(0, 10)}.csv`);
    link.click();
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.programIds.length > 0) count++;
    if (filters.statuses.length > 0) count++;
    if (filters.audienceTypeIds.length > 0) count++;
    if (filters.categoryIds.length > 0) count++;
    if (filters.tags.length > 0) count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.noSessionsOnly) count++;
    return count;
  }, [filters]);

  const SortIndicator = ({ field }: { field: string }) => {
    if (sortField !== field) return <SortAsc size={10} className="ml-1 opacity-20" />;
    return sortDirection === 'asc' 
      ? <SortAsc size={10} className="ml-1 text-blue-600" /> 
      : <SortAsc size={10} className="ml-1 text-blue-600 rotate-180" />;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">
            Engagements Registry <span className="text-slate-300 ml-1">
              {activeFiltersCount > 0 || filters.search ? `(Showing ${engagements?.length || 0} of ${totalCount})` : `(${totalCount})`}
            </span>
          </h1>
          <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mt-1">Strategic Initiatives & Program Delivery</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExport} icon={<Download size={18} />}>Export CSV</Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/import?type=engagements')} icon={<Upload size={18} />}>Import CSV</Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6 bg-slate-50/30">
          <div className="relative flex-1 w-full">
            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Global Search</label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search engagements by name..."
                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-lg font-bold text-slate-900 text-xs outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
                value={filters.search}
                onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <Button 
              onClick={() => setIsFilterOpen(true)}
              className="px-6 py-3 relative group"
              icon={<SlidersHorizontal size={18} />}
            >
              Advanced Filters
              {activeFiltersCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-white shadow-md animate-in zoom-in">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
            
            {(activeFiltersCount > 0 || filters.search) && (
              <Button 
                variant="ghost" 
                className="text-rose-500 hover:bg-rose-50 px-4"
                onClick={() => { setFilters(DEFAULT_FILTERS); setPage(1); }}
                icon={<RotateCcw size={14} />}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        <EngagementFilterSidebar 
          isOpen={isFilterOpen} 
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          onChange={(f) => { setFilters(f); setPage(1); }}
        />

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 text-[8px] font-black uppercase tracking-[0.2em] select-none">
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.name }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('name')}>
                    Engagement <SortIndicator field="name" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.name;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('name', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.program }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('program_id')}>
                    Program <SortIndicator field="program_id" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.program;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('program', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.status }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('status')}>
                    Status <SortIndicator field="status" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.status;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('status', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.sessions }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('tracking_mode')}>
                    Sessions <SortIndicator field="tracking_mode" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.sessions;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('sessions', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.startDate }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('start_date')}>
                    Start Date <SortIndicator field="start_date" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.startDate;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('startDate', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
                <th className="px-6 py-3 relative group" style={{ width: columnWidths.endDate }}>
                  <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('end_date')}>
                    End Date <SortIndicator field="end_date" />
                  </div>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    onMouseDown={(e) => {
                      const startX = e.pageX;
                      const startWidth = columnWidths.endDate;
                      const onMouseMove = (moveEvent: MouseEvent) => {
                        handleResize('endDate', startWidth + (moveEvent.pageX - startX));
                      };
                      const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                      };
                      document.addEventListener('mousemove', onMouseMove);
                      document.addEventListener('mouseup', onMouseUp);
                    }}
                  />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 bg-white">
              {isLoading ? (
                [1, 2, 3, 4, 5].map(i => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={6} className="px-6 py-4"><div className="h-4 bg-slate-50 rounded w-full"></div></td>
                  </tr>
                ))
              ) : engagements?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center text-[10px] font-black text-slate-200 uppercase tracking-widest italic">No matching engagements found</td>
                </tr>
              ) : (
                engagements?.map((eng: any) => (
                  <tr 
                    key={eng.id} 
                    className="hover:bg-slate-50/80 transition-all group cursor-pointer"
                    onClick={() => navigate(`/engagements/${eng.id}`)}
                  >
                    <td className="px-6 py-4">
                      <span className="text-[11px] font-black text-slate-900 uppercase group-hover:text-blue-600 transition-colors break-words">{eng.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Briefcase size={12} className="text-slate-300" />
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight truncate">{eng.programs?.name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border tracking-widest ${eng.status === 'delivered' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                        {eng.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar size={12} className="text-slate-300" />
                        <span className="text-[10px] font-black text-slate-900 uppercase">{eng.sessions?.length || 0} Sessions</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        {formatDateSafe(eng.start_date)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        {formatDateSafe(eng.end_date)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Records per page</span>
              <select 
                value={pageSize} 
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="bg-white border border-slate-200 rounded px-2 py-1 text-[10px] font-bold outline-none focus:ring-2 focus:ring-blue-100"
              >
                {[30, 50, 100, 500, 1000].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
              Showing {Math.min(totalCount, (page - 1) * pageSize + 1)} - {Math.min(totalCount, page * pageSize)} of {totalCount}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === 1} 
              onClick={() => setPage((p: number) => p - 1)}
              className="text-[9px] font-black uppercase tracking-widest"
            >
              Prev
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = page;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 rounded text-[10px] font-black transition-all ${page === pageNum ? 'bg-slate-900 text-[#FFDB00]' : 'bg-white border border-slate-200 text-slate-400 hover:border-slate-900 hover:text-slate-900'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={page === totalPages || totalPages === 0} 
              onClick={() => setPage((p: number) => p + 1)}
              className="text-[9px] font-black uppercase tracking-widest"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementsList;
